import { useDropdown } from '@trimble/solidjs-utils';
import { useConnectProject } from '@trimble/connect-project-providers';
import { Project, Region } from '../types/project-select.types';
import {
  Component,
  createEffect,
  createMemo,
  createSignal,
  Match,
  Show,
  Switch,
} from 'solid-js';
import { t } from '@trimble/i18n';
import { formatCamelOrPascalToTitleCaseWords } from '@trimble/utilities';
import { BtnClasses, Button, IconButton } from '@tcweb/connect-ui';
import RegionSelect from './RegionSelect';
import ProjectSearch from './ProjectSearch';
import { RecentProjects } from './RecentProjects';

export const ProjectSelection: Component = () => {
  const {
    selectProject,
    selectedRegion,
    regions,
    selectedProjectDisplayName,
    selectedProjectId,
    projectDetailsLoading,
  } = useConnectProject();

  const { isOpen, toggle, ref } = useDropdown();
  const [showSearch, setShowSearch] = createSignal<boolean>(false);
  const [localRegion, setLocalRegion] = createSignal<Region | undefined>(
    undefined
  );

  createEffect(() => {
    const region = selectedRegion() ?? regions()?.find((r) => r.isMaster);
    setLocalRegion(region);
  });

  createEffect(() => {
    if (selectedProjectId()) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get('tenantTrn')) return;
    if (!isOpen()) toggle();
  });

  const recentProjectsTitle = createMemo(() => {
    const region = localRegion();
    if (!region) return '';

    return t('shared.ui.projectSelect.recentlyVisitedProjects', {
      region: formatCamelOrPascalToTitleCaseWords(region.location),
    });
  });

  const projectName = createMemo(
    () =>
      selectedProjectDisplayName() ?? t('shared.ui.projectSelect.selectProject')
  );

  const handleProjectSelect = (region: Region, project: Project): void => {
    if (isOpen()) toggle();
    setShowSearch(false);
    selectProject({ location: region.location, projectId: project.id });
  };

  return (
    <div
      class="content text-base-content bg-base-page"
      data-testid="project-selection"
    >
      <div data-testid="project-select" class="w-full" ref={ref}>
        <div class="flex flex-1">
          <Button
            type="hollow"
            size="medium"
            iconPosition="dropdown"
            class={`max-w-full px-1 !text-base-content hover:!bg-base-200
          [&>span]:truncate
          [&>span]:leading-tight
          [&>span]:text-base
          [&>span]:font-normal
          ${projectDetailsLoading() ? '!text-base-muted ' : ''}
          `}
            content={projectName()}
            color={BtnClasses.DEFAULT}
            onClick={toggle}
          />
        </div>

        <Show when={isOpen() && localRegion()} keyed>
          {(region) => (
            <div
              data-testid="project-select-options"
              class="dropdown-pane dropdown is-open w-[26rem] min-h-[24rem] h-full max-w-full p-4 bg-base-page text-base-content"
            >
              <div class="flex flex-col h-full">
                <h2 class="mb-1">
                  {t('shared.ui.projectSelect.changeProject')}
                </h2>

                <div class="flex flex-row items-center">
                  <Switch>
                    <Match when={!showSearch()}>
                      <RegionSelect
                        regions={regions()!}
                        selectedRegion={region}
                        onRegionChange={setLocalRegion}
                      />
                      <IconButton
                        color={BtnClasses.TERTIARY}
                        icon="tc-icon-search"
                        size="icon-medium"
                        title={t('general.search')}
                        clicked={() => setShowSearch(true)}
                      />
                    </Match>
                    <Match when={showSearch()}>
                      <ProjectSearch
                        region={region}
                        onProjectSelect={handleProjectSelect}
                        onCloseClick={() => void setShowSearch(false)}
                      />
                    </Match>
                  </Switch>
                </div>

                <div class="text-base-muted py-2">{recentProjectsTitle()}</div>
                <RecentProjects
                  region={region}
                  currentProjectId={selectedProjectId()}
                  onProjectSelect={handleProjectSelect}
                />
              </div>
            </div>
          )}
        </Show>
      </div>
    </div>
  );
};

export default ProjectSelection;
